import { Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { Transporter } from '../../../../../models/transporter.model';
import { EstimationStatus, LastEstimate } from '../../../../../models/estimate-result-bundle.model';
import { OfferViewDTO } from '../../../../../models/offer-view-dto.type';
import { TranslateService } from '@ngx-translate/core';
import { CartGroupValueItem } from '../../../../../models/cart-group-value-item.model';
import { NumberUtil } from '../../../util/number-util';
import { BaryModalService } from '../../../../../services/bary-modal.service';
import { TransportFormGroup } from '../../../../../models/forms/TransportFormGroup';
import { IEstimationTaxModel } from '../../../../../models/estimation-tax.model';
import { Transport } from '../../../../../models/transport.model';

@Component({
  selector: 'jhi-estimation-price',
  templateUrl: './estimation-price.component.html',
})
export class EstimationPriceComponent {
  readonly #translateService = inject(TranslateService);
  readonly #modalService = inject(BaryModalService);

  @Input() transport!: Transport;
  @Input() transporter!: Transporter;
  @Input() isLoadingData!: boolean;
  @Input() transportForm!: TransportFormGroup;
  @Input() hasNextStep = true;
  @Input() hasPreviousStep = false;
  @Input() lastEstimate?: LastEstimate;
  @Input() insuranceIncluded?: boolean;
  @Input() showButton = true;
  @Input() isPreviousEstimation = false;
  @Input() isNewEstimation = false;
  @Input() offer?: OfferViewDTO;
  @Input() onlyPriceHt = false;
  @Input() keyLabelBasePrice = 'LABEL.TRANSPORT';
  @Input() disabled = false;

  @Output() nextEvent = new EventEmitter();
  @Output() previousEvent = new EventEmitter();
  @Output() requestLoginEvent = new EventEmitter();
  @Output() cancelEvent = new EventEmitter();
  @Output() updateOfferEvent = new EventEmitter();

  public get isDisplayPrice(): boolean {
    return (
      this.lastEstimate?.status === EstimationStatus.OK ||
      ((this.isDisplayQuotation || this.isDisplayQuotationResponse) && typeof this.lastEstimate?.estimation.basePrice !== 'undefined')
    );
  }

  public get isDisplayError(): boolean {
    return this.lastEstimate?.status === EstimationStatus.ERROR;
  }

  public get isDisplayQuotation(): boolean {
    return this.lastEstimate?.status === EstimationStatus.QUOTATION;
  }

  public get isDisplayQuotationResponse(): boolean {
    return this.lastEstimate?.status === EstimationStatus.QUOTATION_RESPONSE;
  }

  public get optionsAmount(): number {
    let amount = 0;
    this.lastEstimate?.estimation.pricingOptions?.forEach(option => {
      if (option.amount) {
        amount += option.amount;
      }
    });
    return amount;
  }

  public get taxesAmount(): number {
    return this.getOtherTaxes().reduce((amount, tax) => amount + (tax.amount || 0), 0) || 0;
  }

  public getTitleEstimationPrice(): string {
    if (this.isPreviousEstimation) {
      return 'LABEL.PREVIOUS_PRICE';
    }
    if (this.isNewEstimation) {
      return 'LABEL.CURRENT_PRICE';
    }
    return 'LABEL.ESTIMATION';
  }

  public asPricingItems(): CartGroupValueItem[] | undefined {
    return this.lastEstimate?.estimation.pricingOptions?.map(
      it =>
        new CartGroupValueItem({
          nameKey: it.key,
          value: it.amount,
        })
    );
  }

  public asTaxItems(): CartGroupValueItem[] | undefined {
    return this.getOtherTaxes().map(
      it =>
        new CartGroupValueItem({
          nameKey: it.key,
          value: it.amount,
        })
    );
  }

  public getOtherTaxes(): IEstimationTaxModel[] {
    return this.lastEstimate?.estimation.taxes ?? [];
  }

  public asGazoleTaxItems(): CartGroupValueItem[] | undefined {
    const res = new CartGroupValueItem({
      nameKey: this.#translateService.instant('LABEL.FUEL_TAX', { gazoleTaxRate: this.gazoleTaxFormattedValue() }),
      value: this.lastEstimate?.estimation.gazoleTaxAmount?.valueOf()!,
    });
    return [res];
  }

  public nextStep(validating?: boolean): void {
    this.nextEvent.emit(validating);
  }

  public gazoleTaxFormattedValue(): string {
    return NumberUtil.formatNumberToPercentString(this.lastEstimate?.estimation.gazoleTaxRate);
  }

  public previousStep(): void {
    this.previousEvent.emit();
  }

  public changeOffer(): void {
    this.#modalService
      .openChangeOffer(this.transport)
      .action()
      .subscribe(action => {
        if (action.submit) {
          this.transportForm.updateForm(action.value);
          this.updateOfferEvent.emit(action.value);
        }
      });
  }

  public cancel(): void {
    this.cancelEvent.emit();
  }
}
